import { useState, useCallback } from "react";
import { useRecoilState } from "recoil";
import {
  Alert,
  CompleteProfile,
  CreatorProfile,
  Feedback,
  FeedbackRequest,
  LoggedInUser,
  ProfileSummary,
} from "models";
import { CreatorService } from "../../services/creator/creator-service";
import { ProfileSequentialTierPaginator } from "../../services/pagination/ProfilePaginationService";
import {
  creatorProfileAtom,
  profilesummaryAtom,
  creatorAnswersAtom,
  singleUserSummaryAtom,
  isSingleUserSummaryLoadingAtom,
  previousQueryAtom,
  showLoadMoreButtonAtom,
  offsetAtom,
} from "../../data/creator-atoms";
import { useAlerts } from "../alert/use-alert";
import { useNotifications } from "../notifications/use-notifications";
import { Appsettings } from "config";
import { AppRoutes, generateEmailSubject, generateEmailText } from "reusable";

export const useCreator = () => {
  const [creatorProfile, setCreatorProfile] =
    useRecoilState<CreatorProfile | null>(creatorProfileAtom);
  const { sendEmail } = useNotifications();

  // for array of summaries
  const [profileSummaries, setProfileSummaries] =
    useRecoilState<Array<ProfileSummary> | null>(profilesummaryAtom);
  const [isSummaryLoading, setIsSummaryLoading] = useState<boolean>(false);
  // for specific user summary
  const [singleUserSummary, setSingleUserSummary] =
    useRecoilState<ProfileSummary | null>(singleUserSummaryAtom);
  const [isSingleUserSummaryLoading, setIsSingleUserSummaryLoading] =
    useRecoilState<boolean>(isSingleUserSummaryLoadingAtom);

  const [creatorAnswers, setCreatorAnswers] =
    useRecoilState<CompleteProfile>(creatorAnswersAtom);
  const [showLoadMoreButton, setShowLoadMoreButton] = useRecoilState<boolean>(
    showLoadMoreButtonAtom
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFeedbackLoading, setIsFeedbackLoading] = useState<boolean>(false);
  const [isFeedbackRequestLoading, setIsFeedbackRequestLoading] =
    useState<boolean>(false);
  const [previousQuery, setPreviousQuery] =
    useRecoilState<string>(previousQueryAtom);
  const [offset, setOffset] = useRecoilState<number>(offsetAtom); // Use Recoil for offset
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(
    null
  );
  const [oldFeedback, setOldFeedback] = useState<Feedback>();
  const [profilesPerPage] = useState<number>(6); // default for non-all
  const [profilePaginator] = useState(() => new ProfileSequentialTierPaginator()); // For 'all' query

  const { addAlert } = useAlerts();

  const handleError = (alert: Alert) => {
    addAlert(alert);
    setIsLoading(false);
  };

  const getProfile = (userId: string) => {
    const handleResponse = (profile: CreatorProfile) => {
      setCreatorProfile(profile);
      setIsLoading(false);
    };

    if (!isLoading && userId) {
      setIsLoading(true);
      CreatorService.getProfile(userId, handleResponse, handleError);
    }
  };

  const getSummary = (userId: string) => {
    const handleResponse = (profile: ProfileSummary) => {
      setIsSingleUserSummaryLoading(false);
      setSingleUserSummary(profile);
    };

    const handleError = (alert: Alert) => {
      addAlert(alert);
      setIsSingleUserSummaryLoading(false);
    };

    if (!isSingleUserSummaryLoading && userId) {
      setIsSingleUserSummaryLoading(true);
      CreatorService.getSummary(userId, handleResponse, handleError);
    }
  };

  const resetSummaries = () => {
    setProfileSummaries(null);
    setPreviousQuery("");
  };

  const resetCreatorProfile = () => setCreatorProfile(null);

  const resetSingleUserSummary = () => setSingleUserSummary(null);

  const resetCompleteProfile = () => {
    resetCreatorProfile();
    resetSingleUserSummary();
  };

  const searchProfileSummaries = useCallback(
    (
      query: string,
      paramOffset?: number,
      userLattitude?: number,
      userLangitude?: number,
      requestAllCallback?: () => void
    ) => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      const newTimer = setTimeout(async () => {
        if (
          !isSummaryLoading &&
          query?.length &&
          (query?.toLowerCase() !== previousQuery?.toLowerCase() ||
            (paramOffset ? paramOffset > 1 : false))
        ) {
          setPreviousQuery(query);
          setIsSummaryLoading(true);
          const finalOffset = paramOffset || offset;
          setOffset(paramOffset ?? 1);

          // Use sequential tier paginator for 'all' query
          if (query === "all") {
            try {
              // Reset paginator on first page
              if (finalOffset === 1) {
                profilePaginator.reset();
              }

              const paginationResult = await profilePaginator.fetchCombined();
              const profiles = paginationResult.items;

              // Create a map of profiles by userId to prevent duplicates
              const uniqueProfiles = new Map<string, ProfileSummary>();

              // Add existing profiles to the map if we're paginating
              if (paramOffset && paramOffset > 1 && profileSummaries) {
                profileSummaries.forEach((profile) => {
                  if (profile.userId) {
                    uniqueProfiles.set(profile.userId, profile);
                  }
                });
              }

              // Add new profiles to the map
              profiles.forEach((profile) => {
                if (profile.userId) {
                  uniqueProfiles.set(profile.userId, profile);
                }
              });

              // Convert map back to array
              const finalProfiles = Array.from(uniqueProfiles.values());

              // Profiles are already sorted by tier (premium first), just set them
              setProfileSummaries(finalProfiles);
              setShowLoadMoreButton(paginationResult.hasMore);
              setIsSummaryLoading(false);
            } catch (error) {
              console.error("Error fetching profiles:", error);
              addAlert({
                type: "error",
                message: "Failed to fetch profiles"
              });
              setIsSummaryLoading(false);
            }
          } else {
            // For other queries (nearby, etc.), use existing service
            let start = 1;
            let end = profilesPerPage;
            start = (finalOffset - 1) * profilesPerPage + 1;
            end = start + profilesPerPage - 1;

            const handleResponse = (profiles: Array<ProfileSummary>) => {
              setIsSummaryLoading(false);
              if (
                query === "nearby" &&
                profiles?.length === 0 &&
                requestAllCallback
              ) {
                requestAllCallback();
              } else {
                // Create a map of profiles by userId to prevent duplicates
                const uniqueProfiles = new Map<string, ProfileSummary>();

                // Add existing profiles to the map if we're paginating
                if (paramOffset && paramOffset > 1 && profileSummaries) {
                  profileSummaries.forEach((profile) => {
                    if (profile.userId) {
                      uniqueProfiles.set(profile.userId, profile);
                    }
                  });
                }

                // Filter and process new profiles
                const activeProfiles = profiles;
                // Add new profiles to the map
                activeProfiles.forEach((profile) => {
                  if (profile.userId) {
                    uniqueProfiles.set(profile.userId, profile);
                  }
                });

                // Convert map back to array
                const finalProfiles = Array.from(uniqueProfiles.values());

                // Sort premium first for non-'all' queries too
                const isPremium = (profile: ProfileSummary) =>
                  Boolean((profile as any).isSubscribed || (profile as any).isPremiumUser);
                const premiumProfiles = finalProfiles.filter(isPremium);
                const regularProfiles = finalProfiles.filter((profile) => !isPremium(profile));
                const sortedProfiles = [...premiumProfiles, ...regularProfiles];

                // Calculate if we should show load more button
                const showLoadMore = sortedProfiles.length >= profilesPerPage;

                // Set profiles and update load more button
                setProfileSummaries(sortedProfiles);
                setShowLoadMoreButton(showLoadMore);
              }
            };

            const handleError = (error: Alert) => {
              addAlert(error);
              setIsSummaryLoading(false);
            };

            CreatorService.searchProfileSummaries(
              query,
              handleResponse,
              handleError,
              start,
              end,
              userLattitude,
              userLangitude,
              Appsettings.locationSearchRadius
            );
          }
        }
      }, 500);

      setDebounceTimer(newTimer);
    },
    [
      debounceTimer,
      isSummaryLoading,
      previousQuery,
      offset,
      setPreviousQuery,
      setIsSummaryLoading,
      setOffset,
      profileSummaries,
      setProfileSummaries,
      setShowLoadMoreButton,
      addAlert,
      profilesPerPage,
      profilePaginator,
    ]
  );

  const giveFeedback = (
    feedback: Feedback,
    oldFeedbackOfUser: Feedback,
    callback: () => void
  ) => {
    const oldRatings = oldFeedbackOfUser?.ratings?.reduce((acc, r) => {
      return { ...acc, [r.parentId]: r.rating };
    }, {});

    setIsFeedbackLoading(true);
    const handleResponse = () => {
      setIsFeedbackLoading(false);
      // Refresh the profile data to show updated rating count
      if (feedback.to) {
        getProfile(feedback.to);
      }
      callback();
    };

    const handleError = (error: Alert) => {
      addAlert(error);
      setIsFeedbackLoading(false);
    };

    CreatorService.giveFeedback(
      feedback,
      oldRatings,
      handleResponse,
      handleError
    );
  };

  const getFeedback = (userId: string, profileId: string) => {
    setIsFeedbackLoading(true);
    const handleResponse = (feedback: Feedback) => {
      setIsFeedbackLoading(false);
      setOldFeedback(feedback);
    };
    const handleError = (error: Alert, display?: boolean) => {
      if (display) {
        addAlert(error);
      }
      setIsFeedbackLoading(false);
    };

    CreatorService.getFeedback(userId, profileId, handleResponse, handleError);
  };

  const sendFeedbackRequest = async (
    requestDetails: FeedbackRequest,
    loggedInUser: LoggedInUser,
    oldRequests: Array<FeedbackRequest>,
    successCallback: () => void
  ) => {
    console.log('=== SEND FEEDBACK REQUEST STARTED ===');
    console.log('requestDetails:', requestDetails);
    console.log('loggedInUser:', loggedInUser);
    console.log('oldRequests length:', oldRequests.length);
    
    const { userId, name } = loggedInUser;
    const {
      employerName,
      universityName,
      professorName,
      companyName,
      emailId,
    } = requestDetails;
    
    console.log('Extracted data:', { userId, name, employerName, universityName, professorName, companyName, emailId });
    
    setIsFeedbackRequestLoading(true);
    console.log('Set loading to true');
    
    const handleSuccess = () => {
      console.log('=== HANDLE SUCCESS CALLED ===');
      if (creatorProfile) {
        setCreatorProfile({
          ...creatorProfile,
          feedbackRequests: [...oldRequests, requestDetails],
        });
      }
      addAlert({
        type: "success",
        message: "Feedback request sent to provided email address !",
      });
      successCallback();
      setIsFeedbackRequestLoading(false);
      console.log('Set loading to false - success');
    };
    const handleError = (error: Alert, display?: boolean) => {
      console.log('=== HANDLE ERROR CALLED ===', error);
      if (display) {
        addAlert(error);
      }
      setIsFeedbackRequestLoading(false);
      console.log('Set loading to false - error');
    };

    const emailSubject = generateEmailSubject(
      professorName || employerName,
      name
    );

    const link = `https://kydoscope.com${AppRoutes.FEEDBACK.replace(
      ":userId",
      userId
    )}`;

    const emailText = generateEmailText(
      professorName || employerName,
      name,
      companyName || universityName || "",
      link
    );

    console.log('Generated email content:', { emailSubject, link, emailText });
    console.log('isFeedbackLoading:', isFeedbackLoading);

    if (!isFeedbackLoading) {
      console.log('=== PROCEEDING WITH EMAIL SEND ===');
      const emailSuccessCallback = () => {
        console.log('=== EMAIL SUCCESS CALLBACK ===');
        CreatorService.sendFeedbackRequest(
          requestDetails,
          userId,
          oldRequests,
          handleSuccess,
          handleError
        );
      };

      const errorCallback = () => {
        console.error('Email sending failed');
        addAlert({
          message: "Failed to send Email",
          type: "error",
        });
        setIsFeedbackRequestLoading(false);
      };
      
      console.log('Attempting to send email:', { emailText, emailSubject, emailId });
      
      sendEmail(
        {
          text: emailText,
          subject: emailSubject,
          to: emailId,
        },
        emailSuccessCallback,
        errorCallback
      );
    } else {
      console.warn('isFeedbackLoading is true, skipping email send');
      setIsFeedbackRequestLoading(false);
    }
  };

  return {
    getProfile,
    creatorProfile,
    isLoading,
    searchProfileSummaries,
    profileSummaries,
    isSummaryLoading,
    setOffset,
    isFeedbackLoading,
    resetSummaries,
    resetCreatorProfile,
    giveFeedback,
    showLoadMoreButton,
    offset,
    setCreatorAnswers,
    creatorAnswers,
    getSummary,
    singleUserSummary,
    isSingleUserSummaryLoading,
    resetCompleteProfile,
    resetSingleUserSummary,
    getFeedback,
    oldFeedback,
    sendFeedbackRequest,
    isFeedbackRequestLoading,
    previousQuery,
    setCreatorProfile,
  };
};
